<template>
  <div class="recent-pages">
    <header :style="sideGutterStyle" class="text-secondary" v-if="heading">
      {{ heading }}
    </header>
    <main class="mr-1">
      <div v-if="recentPages.length === 0 && $slots.emptyState">
        <slot name="emptyState"></slot>
      </div>
      <template v-for="(page, index) in recentPages" v-else>
        <!-- Initiative Page -->
        <div
          :key="index"
          :data-location="JSON.stringify(page)"
          class="page selectable"
          :style="sideGutterStyle"
          v-if="page.name === ROUTE_NAMES.INITIATIVE && getPageInfo(page).initiativeTitle"
        >
          <div class="flex-shrink-0 icon-wrapper">
            <initiative-icon
              class="initiative-symbol"
              :is-done-and-closed="getPageInfo(page).isDoneAndClosed"
            />
          </div>
          <div>
            <router-link
              @click.native="$emit('item-click')"
              :to="page"
              class="text-medium-1 text-primary text-decoration-none mr-2"
              v-no-capture
              >{{ getPageInfo(page).initiativeTitle }}
            </router-link>
            <div class="text-small text-secondary">
              <div
                class="d-flex align-items-baseline"
                v-if="getPageInfo(page).hasParent"
                v-no-capture
              >
                <initiative-icon
                  class="parent-initiative-symbol"
                  :is-done-and-closed="getPageInfo(page).isParentDoneAndClosed"
                />{{ getPageInfo(page).parentTitle }}
              </div>
              <div v-no-capture v-else>
                <i class="kr-icon-greyed mr-1"></i>{{ getPageInfo(page).krTitle }}
              </div>
            </div>
          </div>
        </div>
        <!-- KR Page -->
        <div
          :key="index"
          :data-location="JSON.stringify(page)"
          class="page selectable"
          :style="sideGutterStyle"
          v-else-if="page.name === ROUTE_NAMES.KR && getPageInfo(page).goalTitle"
          selectable
        >
          <span class="icon-wrapper flex-shrink-0"><i class="kr-icon"></i></span>
          <div>
            <router-link
              @click.native="$emit('item-click')"
              :to="page"
              class="text-medium-1 text-primary text-decoration-none"
              v-no-capture
              >{{ getPageInfo(page).krTitle }}
            </router-link>
            <div class="text-small text-secondary" v-no-capture>
              {{ getPageInfo(page).goalTitle }}
            </div>
          </div>
        </div>
        <!-- Goal Page -->
        <div
          :key="index"
          :data-location="JSON.stringify(page)"
          class="page selectable"
          :style="sideGutterStyle"
          v-else-if="page.name === ROUTE_NAMES.GOAL && getPageInfo(page).goalTitle"
          selectable
        >
          <span class="flex-shrink-0 icon-wrapper"><i class="goal-icon"></i></span>
          <router-link
            @click.native="$emit('item-click')"
            :to="page"
            class="text-medium-1 text-primary text-decoration-none"
            v-no-capture
            >{{ getPageInfo(page).goalTitle }}
          </router-link>
        </div>
        <!-- IdeaList Page -->
        <div
          :key="index"
          :data-location="JSON.stringify(page)"
          class="page selectable"
          :style="sideGutterStyle"
          v-else-if="page.name === ROUTE_NAMES.IDEA_LIST && getPageInfo(page).ideaListTitle"
          selectable
        >
          <span class="flex-shrink-0 icon-wrapper"><i class="idea-list-icon"></i></span>
          <router-link
            @click.native="$emit('item-click')"
            :to="page"
            class="text-medium-1 text-primary text-decoration-none"
            v-no-capture
            >{{ getPageInfo(page).ideaListTitle }}
          </router-link>
        </div>
        <!-- Idea Page -->
        <div
          :key="index"
          :data-location="JSON.stringify(page)"
          class="page selectable"
          :style="sideGutterStyle"
          v-if="page.name === ROUTE_NAMES.IDEA && getPageInfo(page).ideaTitle"
        >
          <div class="flex-shrink-0 icon-wrapper">
            <b-form-checkbox
              size="sm"
              :checked="getPageInfo(page).isDoneAndClosed"
              :disabled="true"
            ></b-form-checkbox>
          </div>
          <div>
            <router-link
              @click.native="$emit('item-click')"
              :to="page"
              class="text-medium-1 text-primary text-decoration-none mr-2"
              v-no-capture
              >{{ getPageInfo(page).ideaTitle }}
            </router-link>
            <span class="text-small text-secondary" v-no-capture>
              <i class="idea-list-icon-greyed mr-1"></i>{{ getPageInfo(page).ideaListTitle }}
            </span>
          </div>
        </div>
      </template>
    </main>
  </div>
</template>

<script>
import { BFormCheckbox } from 'bootstrap-vue'
import InitiativeIcon from '@/components/initiative/InitiativeIcon'
import events from '@/constants/events'
import EventBusMixin from '@/mixins/EventBusMixin'
import { ROUTE_NAMES } from '@/constants/routes'

/**
 * This component shows recently visited pages
 * It gets data from localStorage
 */
export default {
  name: 'RecentPages',
  props: {
    orgId: [String, Number],
    heading: String,
    sideGutter: {
      default: 40,
      type: Number
    }
  },
  mixins: [EventBusMixin],
  data() {
    return {
      recentPages: this.getRecentPages(),
      ROUTE_NAMES
    }
  },
  components: {
    InitiativeIcon,
    BFormCheckbox
  },
  computed: {
    sideGutterStyle() {
      return { paddingLeft: `${this.sideGutter}px`, paddingRight: `${this.sideGutter}px` }
    }
  },
  methods: {
    getRecentPages() {
      let recentPages = localStorage.getItem('recentPages') || '{}'
      recentPages = JSON.parse(recentPages)
      return recentPages[this.orgId] || []
    },
    getValueFor(key, page) {
      let value = page.values?.find(([k]) => k === key) || []
      return value[2]
    },
    getPageInfo(page) {
      switch (page.name) {
        case ROUTE_NAMES.INITIATIVE:
          return this.getValueFor('initiativeId', page) || {}
        case ROUTE_NAMES.KR:
          return this.getValueFor('krId', page) || {}
        case ROUTE_NAMES.GOAL:
          return {
            goalTitle: this.getValueFor('goalId', page)
          }
        case ROUTE_NAMES.IDEA:
          return this.getValueFor('ideaId', page) || {}
        case ROUTE_NAMES.IDEA_LIST:
          return {
            ideaListTitle: this.getValueFor('listId', page)
          }
      }
    },
    onHistoryUpdate() {
      this.recentPages = this.getRecentPages()
    }
  },
  mounted() {
    this.listenToEventBus(events.VISIT_HISTORY_UPDATED, this.onHistoryUpdate)
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

$side-spacing: 40px;

.recent-pages {
  margin-top: 12px;
}

.icon-wrapper {
  width: 24px;
  height: 24px;
  background-position: center;
  margin-right: 7px;
  text-align: center;
  @include flexCenter();
}

.icon-wrapper /deep/ .custom-checkbox {
  padding-left: 28px;
}

.page {
  padding-top: 10px;
  padding-bottom: 10px;
  display: flex;
}

.page.selected {
  background-color: $selectionColor;
}

header {
  padding-bottom: 22px;
}

.goal-icon {
  margin-top: -1px;
  @include dimension(13px, 13px);
  background-size: contain;
}

.kr-icon-greyed,
.idea-list-icon-greyed {
  @include dimension(10px, 10px);
  background-size: contain;
}

.idea-list-icon {
  @include dimension(13px, 14px);
}

.initiative-symbol,
.parent-initiative-symbol {
  margin-top: 2px;
}
.parent-initiative-symbol {
  transform: scale(0.7);
}
</style>
